import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';

/** Extra px required before expanding again, to avoid flicker at the boundary. */
const EXPAND_BUFFER_PX = 24;

/**
 * Desktop inline links vs hamburger: below `lg` always compact; at `lg+`,
 * compact when the link row's intrinsic width exceeds the available slot.
 */
export function useSiteNavCompact(): {
  compact: boolean;
  linksSlotRef: RefObject<HTMLDivElement>;
  linksMeasureRef: RefObject<HTMLDivElement>;
} {
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false }) ?? true;
  const [overflowCompact, setOverflowCompact] = useState(false);
  const linksSlotRef = useRef<HTMLDivElement>(null);
  const linksMeasureRef = useRef<HTMLDivElement>(null);
  const overflowCompactRef = useRef(overflowCompact);
  overflowCompactRef.current = overflowCompact;

  useLayoutEffect(() => {
    if (isMobile) {
      setOverflowCompact(false);
      return;
    }

    const slot = linksSlotRef.current;
    const measure = linksMeasureRef.current;
    if (!slot || !measure) return;

    const update = () => {
      const needed = measure.scrollWidth;
      const available = slot.clientWidth;
      const currentlyCompact = overflowCompactRef.current;
      if (currentlyCompact) {
        setOverflowCompact(needed > available - EXPAND_BUFFER_PX);
      } else {
        setOverflowCompact(needed > available);
      }
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(slot);
    ro.observe(measure);
    return () => ro.disconnect();
  }, [isMobile]);

  return {
    compact: isMobile || overflowCompact,
    // Chakra Box ref types expect RefObject<T>; React 19 useRef(null) is RefObject<T | null>.
    linksSlotRef: linksSlotRef as RefObject<HTMLDivElement>,
    linksMeasureRef: linksMeasureRef as RefObject<HTMLDivElement>,
  };
}
